import { useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Button } from './Button';
import { useFocusTrap, useLockBodyScroll, useOnEscape } from '../lib/hooks';

export type ConfirmTone = 'danger' | 'primary' | 'warning';

export interface ConfirmDialogProps {
  open?: boolean;
  title: ReactNode;
  description?: ReactNode;
  /** Leading icon (e.g. <Icon name="trash" size={24} />). */
  icon?: ReactNode;
  tone?: ConfirmTone;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirmation dialog for destructive/irreversible actions (brief §4.2/§7.1).
 *  role="alertdialog", focus-trapped, Escape = cancel. */
export function ConfirmDialog({
  open = true,
  title,
  description,
  icon,
  tone = 'danger',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  useOnEscape(onCancel, open);
  useLockBodyScroll(open);
  useFocusTrap(ref, open);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="tc-overlay" onMouseDown={onCancel}>
      <div
        ref={ref}
        className="tc-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {icon && <div className={cx('tc-confirm__icon', tone !== 'danger' && `tc-confirm__icon--${tone}`)}>{icon}</div>}
        <div className="tc-confirm__title">{title}</div>
        {description && <div className="tc-confirm__desc">{description}</div>}
        <div className="tc-confirm__actions">
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger-solid' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
